import { Controller } from '@hotwired/stimulus'
import { noop } from 'lodash'
import consumer from '../channels/consumer'

const POLL_SECONDS = 60 // every screen, subscribed or not
const POLL_SECONDS_DEGRADED = 30 // only after a socket we had drops
const EVENT_DEBOUNCE_MS = 300
const MIN_REFRESH_GAP_MS = 5000
const JITTER_MAX_MS = 400

export default class extends Controller {
  static targets = ['grid', 'alldayBar', 'countdown', 'muteBtn', 'errorBanner', 'liveBadge']
  static values = { stationId: Number }

  connect() {
    this.pollSeconds = POLL_SECONDS
    this.remainingSeconds = this.pollSeconds
    this.activeFilter = null
    this.refreshing = false
    this.refreshQueued = false
    this.refreshTimer = null
    this.lastRefreshAt = 0
    this.everConnected = false
    const stored = localStorage.getItem('kds-muted')
    this.muted = stored === null ? false : stored === 'true'
    this.syncMuteButton()
    this.setupAudioUnlock()
    this.startCountdown()
    this.subscribe()
  }

  disconnect() {
    clearInterval(this.countdownTimer)
    clearTimeout(this.refreshTimer)
    if (this.subscription) this.subscription.unsubscribe()
    document.removeEventListener('pointerdown', this.boundUnlockAudio)
  }

  // ── ActionCable ───────────────────────────────────────────────────────
  //
  // The cable carries a hint, never data — an event just means "something on
  // this station changed", and the refresh below does the rest. No station
  // filter (the "All" view) means no subscription: that screen polls.

  subscribe() {
    if (!this.hasStationIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'StationChannel', station_id: this.stationIdValue },
      {
        connected: () => this.onCableConnected(),
        disconnected: () => this.onCableDisconnected(),
        received: envelope => this.onCableEvent(envelope),
      },
    )
  }

  onCableConnected() {
    this.setPollSeconds(POLL_SECONDS)
    this.syncLiveBadge(true)
    // Closes the reconnect gap, but only on a RE-connect: the page was just
    // server-rendered, so the first connect has nothing to catch up on.
    if (this.everConnected) this.scheduleRefresh()
    this.everConnected = true
  }

  onCableDisconnected() {
    this.setPollSeconds(POLL_SECONDS_DEGRADED)
    this.syncLiveBadge(false)
  }

  onCableEvent(envelope) {
    if (envelope.v !== 1) return
    this.scheduleRefresh()
  }

  setPollSeconds(seconds) {
    if (this.pollSeconds === seconds) return
    this.pollSeconds = seconds
    // Tightening shouldn't wait out the longer interval already counting down
    this.remainingSeconds = Math.min(this.remainingSeconds, seconds)
  }

  syncLiveBadge(live) {
    if (this.hasLiveBadgeTarget) this.liveBadgeTarget.hidden = !live
  }

  // ── Station filter ────────────────────────────────────────────────────

  filterStation(event) {
    const stationId = event.target.value
    const url = new URL(window.location.href)
    if (stationId) {
      url.searchParams.set('station_id', stationId)
    } else {
      url.searchParams.delete('station_id')
    }
    window.location.href = url.toString()
  }

  // ── All Day item filter ───────────────────────────────────────────────

  filterItem(event) {
    const itemName = event.currentTarget.dataset.itemName
    if (this.activeFilter === itemName) {
      this.activeFilter = null
    } else {
      this.activeFilter = itemName
    }
    this.applyFilter()
  }

  // Re-applies (or clears) the active filter against current DOM state.
  // Called after filterItem and after any merge/swap that replaces card HTML.
  applyFilter() {
    const allPills = this.element.querySelectorAll('.kds-allday-item')
    const allCards = this.element.querySelectorAll('.kds-station-chit')
    const itemName = this.activeFilter

    if (!itemName) {
      allPills.forEach(p => p.classList.remove('kds-allday-item--active'))
      allCards.forEach(card => {
        card.classList.remove('kds-station-chit--dimmed', 'kds-station-chit--highlighted')
        card.querySelectorAll('.kds-chit-item').forEach(r => r.classList.remove('kds-chit-item--match'))
      })
      return
    }

    allPills.forEach(p => p.classList.toggle('kds-allday-item--active', p.dataset.itemName === itemName))
    allCards.forEach(card => {
      const itemRows = card.querySelectorAll('.kds-chit-item')
      const hasMatch = Array.from(itemRows).some(r => r.dataset.itemName === itemName)
      card.classList.toggle('kds-station-chit--highlighted', hasMatch)
      card.classList.toggle('kds-station-chit--dimmed', !hasMatch)
      itemRows.forEach(r => r.classList.toggle('kds-chit-item--match', r.dataset.itemName === itemName))
    })
  }

  // ── Mute / Unmute ─────────────────────────────────────────────────────

  toggleMute() {
    this.muted = !this.muted
    localStorage.setItem('kds-muted', this.muted)
    this.syncMuteButton()
  }

  syncMuteButton() {
    if (!this.hasMuteBtnTarget) return
    this.muteBtnTarget.querySelector('ion-icon').setAttribute(
      'name',
      this.muted ? 'volume-mute-outline' : 'volume-high-outline'
    )
    this.muteBtnTarget.title = this.muted ? 'Unmute sounds' : 'Mute sounds'
    this.muteBtnTarget.classList.toggle('kds-mute-btn--active', !this.muted)
  }

  // ── On-demand refresh (e.g. after rush) ──────────────────────────────

  onChitRushed() {
    this.scheduleRefresh()
  }

  onChitBumped() {
    this.scheduleRefresh()
  }

  onChitBumpCancelled() {
    this.scheduleRefresh()
  }

  onChitItemChecked() {
    this.scheduleRefresh()
  }

  // ── Refresh scheduling ────────────────────────────────────────────────

  // Every path into a refresh — cable event, local action, poll tick — comes
  // through here. Collapses a flurry into one fetch (a cook checking six items
  // off a chit sends the parent station six events), holds a floor between
  // fetches, and jitters so screens on the same station don't fire in lockstep.
  scheduleRefresh() {
    this.refreshQueued = true
    if (this.refreshing || this.refreshTimer) return

    const sinceLast = Date.now() - this.lastRefreshAt
    const delay = Math.max(
      EVENT_DEBOUNCE_MS + Math.random() * JITTER_MAX_MS,
      MIN_REFRESH_GAP_MS - sinceLast,
    )

    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null
      this.refreshGrid()
    }, delay)
  }

  // Clears before scheduling: a second interval sharing remainingSeconds would
  // decrement it twice a second, and its id would be untrackable to clear.
  startCountdown() {
    clearInterval(this.countdownTimer)
    this.countdownTimer = setInterval(() => {
      this.remainingSeconds -= 1
      if (this.hasCountdownTarget) {
        this.countdownTarget.textContent = this.remainingSeconds
      }
      if (this.remainingSeconds <= 0) {
        // Reset here rather than only in refreshGrid's finally, so a slow
        // response can't leave this <= 0 and re-fire on every later tick.
        this.remainingSeconds = this.pollSeconds
        this.scheduleRefresh()
      }
    }, 1000)
  }

  async refreshGrid() {
    // queue a refresh if we're mid flight, the finally below picks it back up.
    if (this.refreshing) {
      this.refreshQueued = true
      return
    }
    this.refreshing = true
    this.refreshQueued = false
    this.lastRefreshAt = Date.now()

    clearInterval(this.countdownTimer)
    let succeeded = false
    try {
      const response = await fetch(window.location.href, {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      if (!response.ok) return

      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      const newGrid = doc.querySelector('[data-kds-target="grid"]')
      if (newGrid && this.hasGridTarget) {
        this.mergeGrid(newGrid)
      }

      const newAllday = doc.querySelector('[data-kds-target="alldayBar"]')
      if (newAllday && this.hasAlldayBarTarget) {
        this.alldayBarTarget.innerHTML = newAllday.innerHTML
      }

      this.applyFilter()

      succeeded = true
    } finally {
      this.refreshing = false
      if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = succeeded
      this.remainingSeconds = this.pollSeconds
      this.startCountdown()
      if (this.refreshQueued) this.scheduleRefresh()
    }
  }

  // ── Smart grid merge ──────────────────────────────────────────────────
  //
  // Fast path: if the grid is empty, full innerHTML replacement is safe and
  // avoids unnecessary work.
  //
  // Otherwise we use a two-phase "insert-around-pinned" strategy:
  //   1. Strip the grid down to only pinned (counting-down) cards — these
  //      cannot be moved without triggering Stimulus disconnect/reconnect
  //      which kills their bump timer.
  //   2. Walk the server-ordered card list, using insertBefore with a cursor
  //      to slot fresh nodes around the pinned ones. Pinned nodes are never
  //      moved; the cursor simply advances past them.

  mergeGrid(newGrid) {
    const grid = this.gridTarget
    const currentCards = new Map(
      Array.from(grid.querySelectorAll('.kds-station-chit[id]'))
        .map(el => [el.id, el])
    )

    if (currentCards.size === 0) {
      grid.innerHTML = newGrid.innerHTML
      this.notifyNewChits(Array.from(grid.querySelectorAll('.kds-station-chit[id]')))
      return
    }

    const serverCards = Array.from(newGrid.querySelectorAll('.kds-station-chit[id]'))
    const serverIds = new Set(serverCards.map(el => el.id))

    // Remove cards the server no longer has (spare mid-countdown — they self-remove)
    currentCards.forEach((el, id) => {
      if (serverIds.has(id)) return
      if (el.classList.contains('kds-station-chit--counting-down')) return
      el.remove()
    })

    // Identify pinned (counting-down) cards still in the DOM
    const pinnedIds = new Set()
    currentCards.forEach((el, id) => {
      if (el.classList.contains('kds-station-chit--counting-down') && el.isConnected) {
        pinnedIds.add(id)
      }
    })

    // Build the desired node list in server order:
    //   - Pinned cards reuse their live DOM node (preserves Stimulus state)
    //   - Everything else gets fresh HTML from the server response
    const addedCards = []
    const desiredNodes = serverCards.map(serverCard => {
      if (pinnedIds.has(serverCard.id)) {
        return { node: currentCards.get(serverCard.id), pinned: true }
      }

      const tmpl = document.createElement('template')
      tmpl.innerHTML = serverCard.outerHTML
      const fresh = tmpl.content.firstElementChild
      if (!currentCards.has(serverCard.id)) addedCards.push(fresh)
      return { node: fresh, pinned: false }
    })

    // Strip all non-pinned cards — they'll be re-inserted fresh below
    currentCards.forEach((el, id) => {
      if (pinnedIds.has(id) || !el.isConnected) return
      el.remove()
    })

    // Grid now contains only pinned nodes. Walk the desired order and
    // insert fresh nodes around them using a cursor.
    let cursor = grid.firstElementChild
    for (const { node, pinned } of desiredNodes) {
      if (pinned) {
        cursor = node.nextElementSibling
      } else {
        grid.insertBefore(node, cursor)
      }
    }

    this.notifyNewChits(addedCards)
  }

  // ── New chit notifications ────────────────────────────────────────────

  notifyNewChits(newChitElements) {
    if (newChitElements.length === 0) return
    newChitElements.forEach(el => el.classList.add('kds-station-chit--new'))
    this.playNewChitSound()
  }

  // ── Audio unlock (iOS Safari) ─────────────────────────────────────────
  //
  // iOS Safari rejects audio.play() unless playback was started from a
  // user gesture, and the unlock applies per media element — a fresh
  // `new Audio()` per chit can never play there. Keep one persistent
  // element and prime it (muted play → pause) inside the first tap;
  // after that, programmatic plays from the polling refresh are allowed.

  setupAudioUnlock() {
    this.chitAudio = new Audio('/audio/ticket-queue-notify.wav')
    this.chitAudio.preload = 'auto'
    this.boundUnlockAudio = () => this.unlockAudio()
    document.addEventListener('pointerdown', this.boundUnlockAudio, { once: true })
  }

  unlockAudio() {
    this.chitAudio.muted = true
    this.chitAudio.play()
      .then(() => {
        this.chitAudio.pause()
        this.chitAudio.currentTime = 0
      })
      .catch(() => {
        // Unlock failed — try again on the next tap
        document.addEventListener('pointerdown', this.boundUnlockAudio, { once: true })
      })
      .finally(() => {
        this.chitAudio.muted = false
      })
  }

  playNewChitSound() {
    if (this.muted || !this.chitAudio) return
    this.chitAudio.currentTime = 0
    // Rejects if not unlocked yet (no user gesture since page load) — stay silent
    this.chitAudio.play().catch(noop)
  }
}
